import { tokens } from "lib/colors"
import { ReactNode, useEffect, useId, useRef, useState } from "react"
import Icon from "lib/components/Icons"
import ImageComponent from "lib/components/Image"
import FormField, { fieldControlA11yProps } from "lib/components/Form/FormField"
import InputShell from "lib/components/Form/InputShell"
import { resolveFieldStatus } from "lib/components/Form/fieldStyles"

export interface InputFileProps {
  label?: string;
  placeholder: string;
  onChange?(object): void;
  value?: string;
  name: string;
  disabled?: boolean;
  maxLength?: number;
  success?: boolean;
  error?: boolean;
  message?: string;
  /** Neutral hint under the box; replaced by `message` while `error` is set. */
  helperText?: ReactNode
  accept?: string
  loading?: boolean
  /** Upload progress, 0-100. Only rendered while `loading`. */
  progressNumber?: number
  onCancel?(): void;
  /** Turns the trailing action into a download of `value` instead of a re-upload. */
  isDownload?: boolean
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif"]

/** Returns the lowercase extension of a URL (query string stripped), or "" when there is none. */
function extensionOf(value: string): string {
  const withoutQuery = value.split("?")[0]
  const parts = withoutQuery.split(".")
  return parts.length > 1 ? parts.pop()?.toLowerCase() ?? "" : ""
}

/** Picks the label colour: an empty field reads as a primary action, a picked file as a value; disabled dims either. */
function resolveLabelColor(hasValue: boolean, disabled: boolean): string {
  if (disabled) return "text-neutral-50"
  return hasValue ? "text-neutral-70" : "text-primary"
}

function fileNameOf(value: string): string {
  const withoutQuery = value.split("?")[0]
  return withoutQuery.substring(withoutQuery.lastIndexOf("/") + 1)
}

/**
 * Renders a file picker as a design-system field.
 *
 * Shows a 24px placeholder tile and an "Upload File" label while empty; the file-type icon (or the
 * image at 24×24), the file name and a re-upload/download action once filled; a progress bar with a
 * cancel action while `loading`.
 */
const InputFile = ({
  label, placeholder, onChange, onCancel, value, name, disabled = false, maxLength, success, error, message, helperText, accept = "application/pdf", loading, progressNumber = 0, isDownload,
}: InputFileProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null)
  const [widthValue, setWidthValue] = useState("0%")
  const controlId = useId()
  const status = resolveFieldStatus(error, success)
  const helper = status === "danger" && message ? message : helperText
  const extension = value ? extensionOf(value) : ""

  useEffect(() => {
    if (progressNumber > 0) {
      setWidthValue(`${progressNumber}%`)
    }

    return () => setWidthValue("0%")
  }, [progressNumber])

  const openFilePicker = () => inputFileRef.current?.click()

  const renderPreview = () => {
    if (!value) return <span className="h-6 w-6 shrink-0 rounded bg-neutral-50" />
    if (extension === "pdf") return <Icon pathName="pdf-new" height="24" width="24" />
    if (["doc", "docx"].includes(extension)) return <Icon pathName="docx-new" height="24" width="24" />
    if (["xls", "xlsx"].includes(extension)) return <Icon pathName="xls" height="24" width="24" />
    if (IMAGE_EXTENSIONS.includes(extension)) {
      // `width`/`height` are required: without them ImageComponent falls back to next/image
      // layout="fill", which positions against the shell and stretches across the whole field.
      return (
        <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded">
          <ImageComponent src={value} width={24} height={24} objectFit="cover" alt={fileNameOf(value)} />
        </span>
      )
    }
    return <Icon iconName="document-text" height={24} width={24} />
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex h-full w-full items-center gap-2 px-2">
          <span className="h-6 w-6 shrink-0 rounded bg-neutral-50" />
          <div className="h-[10px] w-full overflow-hidden rounded-lg border border-neutral-40">
            <div
              className="h-full rounded-lg bg-primary transition-all duration-500"
              style={{ width: widthValue }}
            />
          </div>
        </div>
      )
    }

    return (
      <button
        {...fieldControlA11yProps({ controlId, status, hasHelper: Boolean(helper) })}
        type="button"
        // `disabled` means "no re-upload": call-sites render a read-only viewer with `isDownload disabled`,
        // so an existing file must stay openable.
        disabled={disabled && !value}
        onClick={() => {
          if (value) window.open(value, "_blank", "noopener,noreferrer")
          else openFilePicker()
        }}
        className="flex h-full min-w-0 flex-1 items-center gap-2 px-2 text-sm disabled:cursor-not-allowed">
        {renderPreview()}
        <span className={`truncate ${resolveLabelColor(Boolean(value), disabled)}`}>
          {value ? fileNameOf(value) : "Upload File"}
        </span>
      </button>
    )
  }

  const renderTrailing = () => {
    if (loading) {
      return (
        <button type="button" onClick={onCancel} aria-label="Cancel upload" className="flex items-center">
          <Icon iconName="close" width={20} height={20} color="gray" />
        </button>
      )
    }
    return (
      <button
        type="button"
        disabled={disabled && !isDownload}
        onClick={() => {
          if (isDownload && value) window.open(value, "_blank", "noopener,noreferrer")
          else openFilePicker()
        }}
        aria-label={isDownload ? "Download file" : "Upload file"}
        className="flex items-center disabled:cursor-not-allowed">
        <Icon iconName={isDownload ? "download" : "upload"} width={20} height={20} color={isDownload ? tokens.neutral[50] : undefined} />
      </button>
    )
  }

  return (
    <FormField
      controlId={controlId}
      label={label}
      name={name}
      status={status}
      disabled={disabled}
      helperText={helperText}
      message={message}>
      <InputShell status={status} disabled={disabled} trailingIcon={renderTrailing()}>
        {renderContent()}
        <input
          placeholder={placeholder}
          onChange={(event) => onChange?.(event)}
          name={name}
          disabled={disabled}
          type="file"
          hidden
          maxLength={maxLength}
          accept={accept}
          ref={inputFileRef}
        />
      </InputShell>
    </FormField>
  )
}

export default InputFile
